export type PasswordStrength = 'weak' | 'fair' | 'good' | 'strong' | 'very-strong'

export interface PasswordStrengthResult {
  score: number // 0-100
  strength: PasswordStrength
  feedback: string[]
  color: string
}

export function calculatePasswordStrength(password: string): PasswordStrengthResult {
  if (!password) {
    return {
      score: 0,
      strength: 'weak',
      feedback: ['Enter a passphrase'],
      color: 'bg-gray-400'
    }
  }

  let score = 0
  const feedback: string[] = []

  // Length scoring (max 40 points)
  const length = password.length
  if (length < 8) {
    score += length * 2
    feedback.push('Use at least 8 characters')
  } else if (length < 12) {
    score += 16 + (length - 8) * 3
    feedback.push('Consider using 12+ characters')
  } else if (length < 16) {
    score += 28 + (length - 12) * 2
  } else {
    score += Math.min(40, 36 + (length - 16))
  }

  // Character variety scoring (max 40 points)
  let varietyScore = 0
  const hasLowercase = /[a-z]/.test(password)
  const hasUppercase = /[A-Z]/.test(password)
  const hasNumbers = /\d/.test(password)
  const hasSymbols = /[^a-zA-Z0-9\s]/.test(password)
  const hasSpaces = /\s/.test(password)

  if (hasLowercase) varietyScore += 8
  if (hasUppercase) varietyScore += 8
  if (hasNumbers) varietyScore += 8
  if (hasSymbols) varietyScore += 12
  if (hasSpaces) varietyScore += 4 // Spaces indicate phrase-like passwords

  score += varietyScore

  // Add feedback for missing character types
  if (!hasLowercase && !hasUppercase) {
    feedback.push('Add some letters')
  } else if (!hasLowercase) {
    feedback.push('Add lowercase letters')
  } else if (!hasUppercase) {
    feedback.push('Add uppercase letters')
  }
  
  if (!hasNumbers) {
    feedback.push('Add numbers')
  }
  
  if (!hasSymbols) {
    feedback.push('Add special characters')
  }

  // Pattern detection (max 20 points, but can reduce score)
  let patternPenalty = 0
  
  // Check for common patterns
  const commonPatterns = [
    /^[a-z]+$/i, // Only letters
    /^\d+$/, // Only numbers
    /^[a-z]+\d+$/i, // Letters followed by numbers
    /^\d+[a-z]+$/i, // Numbers followed by letters
    /^(.)\1+$/, // Repeating character
    /^(..+)\1+$/, // Repeating pattern
    /^123|234|345|456|567|678|789|890/, // Sequential numbers
    /^abc|bcd|cde|def|efg|fgh/i, // Sequential letters
    /password|passphrase|secret|admin|user|login/i, // Common words
  ]

  for (const pattern of commonPatterns) {
    if (pattern.test(password)) {
      patternPenalty += 10
      break
    }
  }

  // Check for keyboard patterns
  const keyboardPatterns = ['qwerty', 'asdfgh', 'zxcvbn', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm']
  for (const pattern of keyboardPatterns) {
    if (password.toLowerCase().includes(pattern)) {
      patternPenalty += 15
      feedback.push('Avoid keyboard patterns')
      break
    }
  }

  // Check for repeated characters
  if (/(.)\1{2,}/.test(password)) {
    patternPenalty += 5
    feedback.push('Avoid repeated characters')
  }

  // Bonus for passphrase-like patterns (20 points)
  const wordCount = password.split(/\s+/).filter(w => w.length > 2).length
  if (wordCount >= 3) {
    score += 20
    if (feedback.length === 0) {
      feedback.push('Great passphrase!')
    }
  } else if (wordCount >= 2) {
    score += 10
  }

  // Apply pattern penalty
  score = Math.max(0, score - patternPenalty)

  // Normalize score to 0-100
  score = Math.min(100, score)

  // Determine strength level and color
  let strength: PasswordStrength
  let color: string

  if (score < 20) {
    strength = 'weak'
    color = 'bg-danger-red'
  } else if (score < 40) {
    strength = 'fair'
    color = 'bg-warning-yellow'
  } else if (score < 60) {
    strength = 'good'
    color = 'bg-info-blue'
  } else if (score < 80) {
    strength = 'strong'
    color = 'bg-primary/70'
  } else {
    strength = 'very-strong'
    color = 'bg-primary'
  }

  // Clear feedback if password is strong enough
  if (score >= 60 && feedback.length > 0) {
    feedback.length = 0
    if (score >= 80) {
      feedback.push('Excellent passphrase!')
    } else {
      feedback.push('Good passphrase')
    }
  }

  // If no specific feedback, provide general guidance
  if (feedback.length === 0 && score < 60) {
    feedback.push('Keep improving your passphrase')
  }

  return {
    score,
    strength,
    feedback,
    color
  }
}

// Utility function to get strength label
export function getStrengthLabel(strength: PasswordStrength): string {
  const labels: Record<PasswordStrength, string> = {
    'weak': 'Weak',
    'fair': 'Fair',
    'good': 'Good',
    'strong': 'Strong',
    'very-strong': 'Very Strong'
  }
  return labels[strength]
}